import type { GameStateData } from '../game/GameState.js';

type ScoreSlice = Pick<GameStateData, 'ruleSet' | 'scores' | 'teamMap'>;

/** Every seat sharing `seat`'s team (just `[seat]` in a free-for-all). */
export function teamSeatsOf(state: Pick<GameStateData, 'teamMap'>, seat: number): readonly number[] {
  const { teamOf } = state.teamMap;
  const team = teamOf[seat] ?? seat;
  const seats: number[] = [];
  for (let s = 0; s < teamOf.length; s += 1) {
    if ((teamOf[s] ?? s) === team) seats.push(s);
  }
  return seats;
}

export function isPenaltyScoring(state: Pick<GameStateData, 'ruleSet'>): boolean {
  return state.ruleSet.scoring === 'penalty';
}

/** Sum of the team's recorded scores. */
export function teamTotal(state: ScoreSlice, seat: number): number {
  return teamSeatsOf(state, seat).reduce((sum, s) => sum + (state.scores[s] ?? 0), 0);
}

/**
 * Score shown on a seat's label. Penalty scoring writes a partnership's
 * penalty against its lowest seat only, so both partners show the team total
 * — otherwise the higher seat would sit on 0 all match. Fives keeps the
 * per-seat number it has always shown.
 */
export function seatDisplayScore(state: ScoreSlice, seat: number): number {
  if (isPenaltyScoring(state)) return teamTotal(state, seat);
  return state.scores[seat] ?? 0;
}

/** i18n key of the label printed next to the seat's score. */
export function scoreLabelKey(
  state: Pick<GameStateData, 'ruleSet'>,
): 'playerInfo.score' | 'playerInfo.penalty' {
  return isPenaltyScoring(state) ? 'playerInfo.penalty' : 'playerInfo.score';
}
